import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import clsx from 'clsx';

import { ProjectTriageRow } from './project-triage-row.js';

import type { ProjectTriageItem } from './project-triage-utils.js';

export interface ProjectTriageGroupProps {
	title: string;
	items: ProjectTriageItem[];
	orgName: string;
	/** Highlight the group (title, count) in the accent colour — used for "Needs you". */
	accent?: boolean;
	/** Render rows more quietly — used for the lower-priority "All projects" group. */
	dimmed?: boolean;
	collapsed: boolean;
	onToggleCollapsed: () => void;
	/** When the group is empty, show a compact "all caught up" line instead of nothing. */
	caughtUp?: boolean;
	/** Sizing classes from the list controlling this section's height and its own scroll region. */
	className?: string;
}

/**
 * A collapsible triage section: a fixed header carrying the group title and a
 * count pill, over an independently-scrolling stack of project rows (or, for an
 * empty "Needs you" group, a slim "all caught up" line).
 */
export function ProjectTriageGroup({
	title,
	items,
	orgName,
	accent = false,
	dimmed = false,
	collapsed,
	onToggleCollapsed,
	caughtUp = false,
	className,
}: ProjectTriageGroupProps) {
	const isEmpty = items.length === 0;

	return (
		<section className={clsx('flex min-h-0 flex-col', className)}>
			<ButtonBase
				onClick={onToggleCollapsed}
				className='flex w-full shrink-0 items-center justify-start gap-2 rounded px-0.5 py-1.5 text-left'
				aria-expanded={!collapsed}
			>
				<ExpandMoreRoundedIcon
					className={clsx('text-gray-a9 transition-transform', collapsed && '-rotate-90')}
					fontSize='small'
				/>
				<span
					className={clsx(
						'text-[11px] font-semibold tracking-wider uppercase',
						accent ? 'text-amber-a11' : 'text-gray-a11',
					)}
				>
					{title}
				</span>
				<Chip label={items.length} variant='outlined' color={accent ? 'warning' : 'gray'} className='tabular-nums' />
			</ButtonBase>

			{!collapsed &&
				(isEmpty && caughtUp ?
					<div className='flex items-center gap-1.5 pt-2 pl-0.5 text-gray-a10'>
						<CheckRoundedIcon fontSize='small' className='text-green-a11' />
						<span>All caught up — nothing needs your review.</span>
					</div>
				:	<div className='min-h-0 flex-1 overflow-y-auto pt-2.5'>
						<div
							className={clsx(
								'divide-y',
								accent ? 'divide-gray-a4 overflow-hidden rounded-lg border border-gray-a6 bg-gray-2' : 'divide-gray-a3',
							)}
						>
							{items.map((item) => (
								<ProjectTriageRow key={item.id} project={item} orgName={orgName} dimmed={dimmed} />
							))}
						</div>
					</div>)}
		</section>
	);
}
